import { useConfigHelper } from '@ilteducation/config';
import type { RegisterEventsBody } from '@ilteducation/activity-stream-types';
import axios from 'axios';

// Augmentations merge, so re-declare rather than import ilt-auth's runtime for a type.
declare module 'axios' {
  export interface AxiosRequestConfig {
    noAuth?: boolean;
  }
}

const { getApiUrl } = useConfigHelper();

export type ShareTarget = {
  shareId: string;
  openId: string;
};

let shareTarget: ShareTarget | null = null;

/**
 * Points the stream at a share opened without login instead of at activity-stream directly, for the whole tab.
 *
 * An anonymous listener has no token, and activity-stream requires one, so the events go to
 * shared-content's public forwarding route and it emits them on the listener's behalf. Nothing else
 * changes: the queue, the batching, the retries and the events themselves are identical, which is
 * why this is a target switch rather than a second sending path.
 *
 * Call it once, after the open resolves, before the player mounts. Pass null to go back to the
 * normal authenticated route.
 */
export const setShareTarget = (target: ShareTarget | null) => {
  shareTarget = target;
};

export const sendEventToActivityStream = async (data: RegisterEventsBody) => {
  if (shareTarget) {
    // noAuth is required, not decorative: ilt-auth's request interceptor cancels any request it
    // cannot attach a token to, which is every request an anonymous listener makes.
    await axios.post(`${getApiUrl('shared-contents')}/public/${shareTarget.shareId}/activity-events`, data, {
      noAuth: true,
      params: { openId: shareTarget.openId },
      adapter: 'fetch',
      fetchOptions: {
        keepalive: true,
      },
    });

    return;
  }

  const hostname = window.location.hostname;
  const hostnameWithoutDots = hostname.replace(/\./g, '');

  await axios.post(`${getApiUrl('activity-stream')}/origin/${hostnameWithoutDots}/events`, data, {
    adapter: 'fetch',
    fetchOptions: {
      keepalive: true,
    },
  });
};
